import {
  Box,
  useTheme,
  TextField,
  Divider,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Autocomplete,
  Tooltip,
  useMediaQuery,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import React, { useState, useEffect, useCallback, useRef } from "react";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { usePageTitle } from "hooks/usePageTitle";
import { useEditLock } from "hooks/useEditLock";
import EditLockDialog from "components/EditLockDialog";

const SERVICE_TAG_TRIGGER_REGEX = /(^|\s)#([a-zA-Z0-9-]*)$/;

const getServiceTagQueryAtCursor = (text, cursorPosition) => {
  if (typeof text !== "string") return null;
  const safeCursor = typeof cursorPosition === "number" ? cursorPosition : text.length;
  const beforeCursor = text.slice(0, safeCursor);
  const match = beforeCursor.match(SERVICE_TAG_TRIGGER_REGEX);

  if (!match) return null;
  return {
    query: (match[2] || "").trim(),
    cursorPosition: safeCursor,
  };
};

const TaskNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { currentUser } = useAuth();
  const { overlayData, setOverlayName, triggerPageRefresh } = useStateContext();
  const { socket } = useWebSocket();

  usePageTitle(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? "New Task"
      : "Edit Task",
  );

  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditable, setIsEditable] = useState(true);
  const descriptionInputRef = useRef(null);

  const isNewTask = overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW;

  // Edit lock for existing tasks only
  const { isLocked, lockedBy, releaseLock } = useEditLock(
    "task",
    isNewTask ? null : overlayData.Task?.id,
  );

  // Error states
  const [titleError, setTitleError] = useState(false);

  // Form states
  const [title, setTitle] = useState(isNewTask ? "" : overlayData.Task.Title);

  const [description, setDescription] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? ""
      : overlayData.Task.Description || "",
  );

  const [status, setStatus] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? "Open"
      : overlayData.Task.Status,
  );

  const [priority, setPriority] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? "Normal"
      : overlayData.Task.Priority,
  );

  const [dueDate, setDueDate] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? null
      : overlayData.Task.DueDate
        ? dayjs(overlayData.Task.DueDate)
        : null,
  );

  const [assignedToUserID, setAssignedToUserID] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? currentUser?.id || ""
      : overlayData.Task.AssignedToUserID || "",
  );

  const [entityType, setEntityType] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? overlayData.Task?.EntityType === "Service" &&
        overlayData.Task?.ServiceType
        ? overlayData.Task.ServiceType
        : overlayData.Task?.EntityType || ""
      : overlayData.Task.EntityType === "Service" &&
          overlayData.Task.ServiceType
        ? overlayData.Task.ServiceType
        : overlayData.Task.EntityType || "",
  );

  const [entityID, setEntityID] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? overlayData.Task?.EntityID || ""
      : overlayData.Task.EntityID || "",
  );

  const [selectedEntity, setSelectedEntity] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW && overlayData.Service
      ? overlayData.Service
      : null,
  );

  const [isCancelled] = useState(
    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? false
      : overlayData.Task.IsCancelled || false,
  );

  // Enum options
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [linguists, setLinguists] = useState([]);
  const [services, setServices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [serviceTagQuery, setServiceTagQuery] = useState("");
  const [serviceTagCursor, setServiceTagCursor] = useState(null);
  const [serviceTagSuggestions, setServiceTagSuggestions] = useState([]);
  const [isServiceTagOpen, setIsServiceTagOpen] = useState(false);
  const [isServiceTagLoading, setIsServiceTagLoading] = useState(false);
  const statuses = ["Open", "In Progress", "Requires Action", "Done"];
  const priorities = ["Low", "Normal", "High", "Urgent"];
  const entityTypes = [
    "Translation",
    "Interpretation",
    "Other Service",
    "Client",
    "Linguist",
    "Invoice",
  ];

  // Fetch members and entities
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [membersRes, clientsRes, linguistsRes, servicesRes, invoicesRes] =
          await Promise.all([
            api.get("/members"),
            api.get("/clients/tasks"),
            api.get("/linguists/tasks"),
            api.get("/services/tasks"),
            api.get("/invoices"),
          ]);

        setMembers(membersRes.data);
        setClients(clientsRes.data);
        setLinguists(linguistsRes.data);
        setServices(servicesRes.data);
        setInvoices(invoicesRes.data);

        // Set selectedEntity if entityID exists (for both new and edit)
        if (entityID && entityType) {
          let entity = null;
          if (entityType === "Client") {
            entity = clientsRes.data.find((client) => client.id === entityID);
          } else if (entityType === "Linguist") {
            entity = linguistsRes.data.find(
              (linguist) => linguist.id === entityID,
            );
          } else if (
            ["Translation", "Interpretation", "Other Service"].includes(
              entityType,
            )
          ) {
            entity = servicesRes.data.find(
              (service) => service.id === entityID,
            );
          } else if (entityType === "Invoice") {
            entity = invoicesRes.data.find(
              (invoice) => invoice.id === entityID,
            );
          }
          if (entity) setSelectedEntity(entity);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [entityID, entityType]);

  // Get entity options based on EntityType
  const getEntityOptions = () => {
    switch (entityType) {
      case "Client":
        return clients;
      case "Linguist":
        return linguists;
      case "Translation":
        return services.filter(
          (service) => service.ServiceCategory === "Translation",
        );
      case "Interpretation":
        return services.filter(
          (service) => service.ServiceCategory === "Interpretation",
        );
      case "Other Service":
        return services.filter(
          (service) => service.ServiceCategory === "Other",
        );
      case "Invoice":
        return invoices;
      default:
        return [];
    }
  };

  // Get entity label based on EntityType
  const getEntityLabel = () => {
    switch (entityType) {
      case "Client":
        return "Client";
      case "Linguist":
        return "Linguist";
      case "Translation":
      case "Interpretation":
      case "Other Service":
        return "Service";
      case "Invoice":
        return "Invoice";
      default:
        return "Entity";
    }
  };

  // Get option label for autocomplete
  const getOptionLabel = (option) => {
    if (!option) return "";
    if (entityType === "Client") {
      return `${option.Account}${
        option.ShortName ? ` (${option.ShortName})` : ""
      }`;
    } else if (entityType === "Linguist") {
      return option.Name;
    } else if (
      ["Translation", "Interpretation", "Other Service"].includes(entityType)
    ) {
      return `${option.ProjectNumber} - ${option.Details || "No details"}`;
    } else if (entityType === "Invoice") {
      return `Invoice ${option.Number}`;
    }
    return "";
  };

  const addNewTask = useCallback((data) => {
    api
      .post("/tasks/create", data)
      .then((response) => {
        setIsSavingChanges(false);

        // Emit socket event for real-time updates
        if (socket && data.EntityID) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: response.data.id,
            entityType: data.EntityType,
            entityID: data.EntityID,
            serviceType: data.ServiceType,
            changeAction: "Created",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("New Task Added Successfully!", theme);
        setOverlayName("");
      })
      .catch((err) => {
        console.log(err);
        setIsSavingChanges(false);
        setIsEditable(true);
      });
  }, [
    socket,
    currentUser.id,
    currentUser.Name,
    currentUser.ImageThumbnail,
    triggerPageRefresh,
    theme,
    setOverlayName,
  ]);

  const updateTask = useCallback((data) => {
    api
      .put(`/tasks/update/${overlayData.Task.id}`, data)
      .then((response) => {
        setIsSavingChanges(false);

        // Emit socket event for real-time updates
        if (socket && data.EntityID) {
          socket.emit("task:saved", {
            modelType: "Task",
            recordId: overlayData.Task.id,
            entityType: data.EntityType,
            entityID: data.EntityID,
            serviceType: data.ServiceType,
            changeAction: "Updated",
            user: {
              id: currentUser.id,
              name: currentUser.Name,
              imageThumbnail: currentUser.ImageThumbnail,
            },
          });
        }

        triggerPageRefresh("Task Updated Successfully", theme);
        setOverlayName("");
      })
      .catch((err) => {
        console.log(err);
        setIsSavingChanges(false);
        setIsEditable(true);
      });
  }, [
    overlayData.Task.id,
    socket,
    currentUser.id,
    currentUser.Name,
    currentUser.ImageThumbnail,
    triggerPageRefresh,
    theme,
    setOverlayName,
  ]);

  const saveChanges = useCallback(() => {
    if (!title.trim()) {
      setTitleError(true);
      return;
    }

    setIsSavingChanges(true);
    setIsEditable(false);

    let mappedEntityType = entityType;
    let mappedServiceType = null;

    if (
      ["Translation", "Interpretation", "Other Service"].includes(entityType)
    ) {
      mappedEntityType = "Service";
      mappedServiceType = entityType;
    }

    const data = {
      Title: title,
      Description: description || null,
      Status: status,
      Priority: priority,
      DueDate: dueDate ? dueDate.toISOString() : null,
      AssignedToUserID: assignedToUserID || null,
      CreatedByUserID: currentUser.id,
      EntityType: mappedEntityType || null,
      EntityID: selectedEntity?.id || null,
      ServiceType: mappedServiceType,
      IsCancelled: isCancelled,
    };

    overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
      ? addNewTask(data)
      : updateTask(data);
  }, [
    title,
    entityType,
    description,
    status,
    priority,
    dueDate,
    assignedToUserID,
    currentUser.id,
    selectedEntity,
    isCancelled,
    overlayData.Action,
    addNewTask,
    updateTask,
  ]);

  const handleDescriptionServiceTagState = useCallback(
    (value, cursorPosition) => {
      if (!isEditable) {
        setIsServiceTagOpen(false);
        return;
      }

      const context = getServiceTagQueryAtCursor(value, cursorPosition);
      if (!context || !context.query) {
        setIsServiceTagOpen(false);
        setServiceTagQuery("");
        setServiceTagCursor(null);
        return;
      }

      setServiceTagQuery(context.query);
      setServiceTagCursor(context.cursorPosition);
      setIsServiceTagOpen(true);
    },
    [isEditable]
  );

  useEffect(() => {
    if (!isServiceTagOpen || !serviceTagQuery) {
      setServiceTagSuggestions([]);
      setIsServiceTagLoading(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsServiceTagLoading(true);
      try {
        const response = await api.get(
          `/services/tag-search?query=${encodeURIComponent(serviceTagQuery)}&limit=10`
        );
        setServiceTagSuggestions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching service tag suggestions:", error);
        setServiceTagSuggestions([]);
      } finally {
        setIsServiceTagLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isServiceTagOpen, serviceTagQuery]);

  const handleSelectServiceTag = useCallback(
    (service) => {
      const projectNumber = service?.ProjectNumber;
      if (projectNumber === null || projectNumber === undefined) return;

      const cursorPosition =
        typeof serviceTagCursor === "number" ? serviceTagCursor : description.length;
      const beforeCursor = description.slice(0, cursorPosition);
      const afterCursor = description.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        SERVICE_TAG_TRIGGER_REGEX,
        (fullMatch, leadingWhitespace) => `${leadingWhitespace}#${projectNumber}`
      );

      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;

      setDescription(nextValue);
      setIsServiceTagOpen(false);
      setServiceTagSuggestions([]);
      setServiceTagQuery("");
      setServiceTagCursor(null);

      requestAnimationFrame(() => {
        const input = descriptionInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [description, serviceTagCursor]
  );

  const attemptSave = useCallback(() => {
    if (isSavingChanges || isLocked) {
      return;
    }

    saveChanges();
  }, [isSavingChanges, isLocked, saveChanges]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA") {
        e.preventDefault();
        attemptSave();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [attemptSave]);

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "90%", md: "75%", lg: "60%" },
          minWidth: { xs: "100%", sm: "600px" },
          maxWidth: "1200px",
          maxHeight: "80%",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0 .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: isMobile ? "0.5rem 1rem" : "0.5rem 2rem",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flex: 1,
              flexDirection: "row",
            }}
          >
            <IconButton
              color="primary"
              size="large"
              onClick={() => setOverlayName("")}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>

            <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
              {overlayData.Action === OVERLAY_NAMES.TASK_ACTION_NEW
                ? "New Task"
                : "Edit Task"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "end",
              alignItems: "center",
              gap: isMobile ? 1 : 0,
              flexDirection: "row",
            }}
          >
            {isMobile ? (
              <>
                <Tooltip title="Discard">
                  <IconButton
                    color="primary"
                    onClick={() => setOverlayName("")}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.background.default,
                      border: `2px solid ${theme.palette.primary.main}`,
                      borderRadius: "12px",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                      },
                    }}
                  >
                    <DisabledByDefaultRoundedIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Save">
                  <IconButton
                    onClick={attemptSave}
                    disabled={isSavingChanges || isLocked}
                    sx={{
                      width: "40px",
                      height: "40px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "12px",
                      color: "white",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.primary.buttonHoverSelected,
                      },
                      "&:disabled": {
                        backgroundColor: theme.palette.grey[400],
                        color: theme.palette.grey[200],
                      },
                    }}
                  >
                    <SaveRoundedIcon />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  endIcon={<DisabledByDefaultRoundedIcon />}
                  onClick={() => setOverlayName("")}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Discard
                </Button>

                <Box p="0 .5rem" />

                <Button
                  loading={isSavingChanges}
                  disabled={isSavingChanges || isLocked}
                  color="primary"
                  variant="contained"
                  size="large"
                  endIcon={<SaveRoundedIcon />}
                  onClick={attemptSave}
                  sx={{
                    borderRadius: "12px",
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: "1rem 2.3rem 1rem 2.5rem",
            height: "100%",
            overflowY: "auto",
            borderRadius: "1.5rem",
            backgroundColor: theme.palette.background.alt,
            "& .MuiTextField-root": {
              m: 0,
              width: "100%",
            },
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h4" fontWeight="bold">
                  Task Information
                </Typography>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  id="Title"
                  label="Title"
                  required
                  error={titleError}
                  value={title}
                  onChange={(event) => {
                    setTitle(event.target.value);
                    setTitleError(event.target.value.trim() === "");
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    disabled={!isEditable}
                    size="small"
                    multiline
                    rows={3}
                    id="Description"
                    label="Description"
                    value={description}
                    inputRef={descriptionInputRef}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setDescription(nextValue);
                      handleDescriptionServiceTagState(
                        nextValue,
                        event.target.selectionStart ?? nextValue.length
                      );
                    }}
                    onKeyUp={(event) => {
                      const target = event.target;
                      const textValue =
                        target && typeof target.value === "string" ? target.value : description;
                      const cursorPosition =
                        target && typeof target.selectionStart === "number"
                          ? target.selectionStart
                          : textValue.length;

                      handleDescriptionServiceTagState(
                        textValue,
                        cursorPosition
                      );
                    }}
                    onClick={(event) => {
                      const target = event.target;
                      const textValue =
                        target && typeof target.value === "string" ? target.value : description;
                      const cursorPosition =
                        target && typeof target.selectionStart === "number"
                          ? target.selectionStart
                          : textValue.length;

                      handleDescriptionServiceTagState(
                        textValue,
                        cursorPosition
                      );
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        setIsServiceTagOpen(false);
                      }, 120);
                    }}
                  />

                  {isServiceTagOpen && (
                    <Paper
                      elevation={3}
                      sx={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        maxHeight: 280,
                        overflowY: "auto",
                      }}
                    >
                      {isServiceTagLoading ? (
                        <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : serviceTagSuggestions.length > 0 ? (
                        <List dense disablePadding>
                          {serviceTagSuggestions.map((service) => (
                            <ListItemButton
                              key={service.id}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                handleSelectServiceTag(service);
                              }}
                            >
                              <ListItemText
                                primary={`#${service.ProjectNumber}`}
                                secondary={service.Details || "No details"}
                              />
                            </ListItemButton>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ p: 1.5 }}>
                          No matching services
                        </Typography>
                      )}
                    </Paper>
                  )}
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id="Status"
                  label="Status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                    },
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                  }}
                >
                  {statuses.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id="Priority"
                  label="Priority"
                  value={priority}
                  onChange={(event) => {
                    setPriority(event.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                    },
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                  }}
                >
                  {priorities.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <DatePicker
                  disabled={!isEditable}
                  label="Due Date"
                  value={dueDate}
                  onChange={(newValue) => {
                    setDueDate(newValue);
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id="AssignedTo"
                  label="Assigned To"
                  value={assignedToUserID}
                  onChange={(event) => {
                    setAssignedToUserID(event.target.value);
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                    },
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Unassigned</em>
                  </MenuItem>
                  {members.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.Name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Divider />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="h4" fontWeight="bold">
                  Related Entity
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  disabled={!isEditable}
                  size="small"
                  select
                  id="EntityType"
                  label="Entity Type"
                  value={entityType}
                  onChange={(event) => {
                    setEntityType(event.target.value);
                    // Clear selected entity when entity type changes
                    setSelectedEntity(null);
                    setEntityID("");
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                    },
                    "& .MuiInputBase-input": {
                      padding: "8.5px 12px !important",
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {entityTypes.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  disabled={!isEditable || !entityType}
                  size="small"
                  options={getEntityOptions()}
                  value={selectedEntity}
                  onChange={(event, newValue) => {
                    setSelectedEntity(newValue);
                    setEntityID(newValue?.id || "");
                  }}
                  getOptionLabel={getOptionLabel}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={getEntityLabel()}
                      size="small"
                      placeholder={
                        entityType
                          ? `Search ${getEntityLabel()}...`
                          : "Select Entity Type first"
                      }
                    />
                  )}
                  noOptionsText={
                    entityType
                      ? `No ${getEntityLabel()}s found`
                      : "Select Entity Type first"
                  }
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </Box>

      {/* Edit Lock Dialog */}
      <EditLockDialog
        open={isLocked}
        lockedBy={lockedBy}
        onClose={() => {
          releaseLock();
          setOverlayName("");
        }}
      />
    </Box>
  );
};

export default TaskNewEdit;
